import React, { useEffect, useState } from "react";
import API from "../utils/api";
import { FaWhatsapp, FaCoins, FaCopy, FaCreditCard } from "react-icons/fa";
import { motion } from "framer-motion";
import { useCountry } from "../context/CountryContext";

// ===== YOUR NOMBA PAYMENT LINK (fallback when vendor has no link) =====
const NOMBA_PAYMENT_LINK = "https://pay.nomba.com/pay/6850174465";

const PurchasePage = () => {
  const { isGhana, config } = useCountry();

  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [amounts, setAmounts] = useState({});
  const [moneyAmounts, setMoneyAmounts] = useState({});
  const [transactions, setTransactions] = useState({});
  const [countdowns, setCountdowns] = useState({});
  const [ordersSold, setOrdersSold] = useState({});
  const [onlineStatus, setOnlineStatus] = useState({});
  const [showOrderModal, setShowOrderModal] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [showPaymentChoice, setShowPaymentChoice] = useState(null);

  const [showLinkAmountModal, setShowLinkAmountModal] = useState(null);
  const [showLinkInstructions, setShowLinkInstructions] = useState(null);
  const [linkCountdown, setLinkCountdown] = useState(5);
  const [showLinkPending, setShowLinkPending] = useState(null);
  const [toast, setToast] = useState("");

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3500);
  };

  const theme = isGhana
    ? {
        bgGradient: "from-red-900 via-black to-yellow-900",
        titleColor: "text-yellow-400",
        titleGlow: "drop-shadow-[0_0_10px_#facc15]",
        cardBorder: "border-yellow-600",
        cardHoverShadow: "hover:shadow-yellow-400/50",
        vendorNameColor: "text-yellow-400",
        vendorNameGlow: "drop-shadow-[0_0_6px_#facc15]",
        infoBoxBorder: "border-yellow-500",
        inputBorder: "border-yellow-500",
        inputFocus: "focus:ring-yellow-400",
        buyBtn: "from-yellow-500 to-red-500 hover:from-red-500 hover:to-yellow-500",
        modalTitleColor: "text-yellow-400",
        modalBtnGradient: "from-yellow-500 to-red-500 hover:from-red-500 hover:to-yellow-500",
        payBankBtnGradient: "from-yellow-500 to-orange-500 hover:from-orange-500 hover:to-yellow-500",
        primaryGradient: "from-yellow-400 to-red-500",
        currencySymbol: "₵",
        flag: "🇬🇭",
        paymentTitle: "Buy Coins from Ghanaian Vendors",
      }
    : {
        bgGradient: "from-cyan-900 via-black to-purple-900",
        titleColor: "text-cyan-400",
        titleGlow: "drop-shadow-[0_0_10px_cyan]",
        cardBorder: "border-cyan-600",
        cardHoverShadow: "hover:shadow-cyan-400/50",
        vendorNameColor: "text-cyan-400",
        vendorNameGlow: "drop-shadow-[0_0_6px_cyan]",
        infoBoxBorder: "border-cyan-500",
        inputBorder: "border-cyan-500",
        inputFocus: "focus:ring-cyan-400",
        buyBtn: "from-cyan-500 to-blue-500 hover:from-blue-500 hover:to-cyan-500",
        modalTitleColor: "text-cyan-400",
        modalBtnGradient: "from-cyan-500 to-blue-500 hover:from-blue-500 hover:to-cyan-500",
        payBankBtnGradient: "from-blue-500 to-cyan-500 hover:from-cyan-600 hover:to-blue-600",
        primaryGradient: "from-cyan-400 to-blue-500",
        currencySymbol: "₦",
        flag: "🇳🇬",
        paymentTitle: "Buy Coins from Vendors",
      };

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const res = await API.get(`/vendors/list?country=${isGhana ? "GH" : "NG"}`);
        const vendorsWithTrust = res.data.map((v, idx) => ({
          ...v,
          trustworthy: [99, 97, 95][idx % 3],
        }));
        setVendors(vendorsWithTrust);

        const initialOrders = {};
        const initialStatus = {};
        vendorsWithTrust.forEach((v) => {
          initialOrders[v._id] = Math.floor(Math.random() * (10000 - 500 + 1)) + 500;
          initialStatus[v._id] = { seconds: 0, online: true };
        });
        setOrdersSold(initialOrders);
        setOnlineStatus(initialStatus);
      } catch (err) {
        console.error("Failed to fetch vendors", err);
      } finally {
        setLoading(false);
      }
    };
    fetchVendors();
    const interval = setInterval(fetchVendors, 10000);
    return () => clearInterval(interval);
  }, [isGhana]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdowns((prev) => {
        const newCounts = { ...prev };
        Object.keys(newCounts).forEach((vendorId) => {
          if (newCounts[vendorId] > 0) newCounts[vendorId] -= 1;
        });
        return newCounts;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setOrdersSold((prev) => {
        const newOrders = { ...prev };
        Object.keys(newOrders).forEach((vendorId) => {
          newOrders[vendorId] += 2;
        });
        return newOrders;
      });
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!showLinkInstructions) return;
    setLinkCountdown(5);
    const timer = setInterval(() => {
      setLinkCountdown((p) => {
        if (p <= 1) {
          clearInterval(timer);
          return 0;
        }
        return p - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [showLinkInstructions]);

  const handleBuy = async (vendor) => {
    const amount = Number(amounts[vendor._id]);
    if (!amount || amount <= 0) return showToast("❌ Please enter a valid amount.");
    try {
      const res = await API.post("/orders", {
        vendorId: vendor._id,
        amount,
        paymentType: "bank",
        country: isGhana ? "GH" : "NG",
      });
      setModalData({
        vendor,
        amount,
        money: res.data.money,
        vendorBank: res.data.vendorBank || {},
      });
      setShowOrderModal(vendor._id);
      setTransactions((prev) => ({ ...prev, [vendor._id]: true }));
      setCountdowns((prev) => ({ ...prev, [vendor._id]: 300 }));
      setAmounts((prev) => ({ ...prev, [vendor._id]: "" }));
    } catch (err) {
      console.error(err);
      showToast("❌ " + (err.response?.data?.message || "Failed to create order."));
    }
  };

  // ============= VENDOR LINK FLOW =============
  // Uses vendor's own paymentLink if set, otherwise falls back to your Nomba link
  const getPaymentLink = (vendor) => {
    return vendor.vendorDetails?.paymentLink || NOMBA_PAYMENT_LINK;
  };

  const openLinkAmountModal = (vendor) => {
    setShowLinkAmountModal(vendor);
  };

  const proceedToLinkInstructions = async () => {
    const vendor = showLinkAmountModal;
    const money = Number(moneyAmounts[vendor._id]);
    if (!money || money <= 0) return showToast("❌ Please enter a valid amount.");

    try {
      const res = await API.post("/orders", {
        vendorId: vendor._id,
        moneyAmount: money,
        paymentType: "link",
        country: isGhana ? "GH" : "NG",
      });

      setShowLinkInstructions({
        vendor,
        moneyAmount: money,
        coins: res.data.coins,
        order: res.data.order,
      });
      setShowLinkAmountModal(null);
    } catch (err) {
      console.error(err);
      showToast("❌ " + (err.response?.data?.message || "Failed to create order."));
    }
  };

  const copyMoneyAmount = (money) => {
    // Nomba accepts whole naira directly — no kobo multiplier needed
    navigator.clipboard.writeText(String(money));
    showToast(`✅ ${theme.currencySymbol}${money.toLocaleString()} copied!`);
  };

  const goToVendorLink = () => {
    const { vendor, moneyAmount, coins } = showLinkInstructions;
    const link = getPaymentLink(vendor);
    setShowLinkPending({ vendor, moneyAmount, coins });
    setShowLinkInstructions(null);
    setMoneyAmounts((prev) => ({ ...prev, [vendor._id]: "" }));
    window.open(link, "_blank");
  };

  const handleModalOk = () => setShowOrderModal(null);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showToast("✅ Copied to clipboard!");
  };

  const getWhatsappUrl = (whatsapp, vendorName, msg) => {
    if (!whatsapp) return null;
    const number = whatsapp.replace(/\D/g, "");
    const message = encodeURIComponent(
      msg || `Hi ${vendorName || "vendor"}, I want to buy coins.`
    );
    return `https://wa.me/${number}?text=${message}`;
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-black via-gray-900 to-black text-white">
        <p className={`animate-pulse text-lg ${theme.titleColor} font-bold`}>
          Loading {config.name} vendors...
        </p>
      </div>
    );

  return (
    <div className="min-h-screen relative bg-black/90 overflow-hidden p-6">
      {/* TOAST */}
      {toast && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="fixed top-20 left-1/2 transform -translate-x-1/2 z-[60] bg-black/90 backdrop-blur-xl border border-white/10 text-white px-6 py-3 rounded-2xl text-sm shadow-2xl max-w-[90%] text-center"
        >
          {toast}
        </motion.div>
      )}

      <div className="fixed top-4 right-4 z-50 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-900/60 border border-zinc-700/50 backdrop-blur-md">
        <span className="text-base leading-none">{theme.flag}</span>
        <span className="text-xs text-slate-300 font-medium">{config.name}</span>
      </div>

      <div className="absolute inset-0 -z-10">
        <div className={`w-full h-full bg-gradient-to-tr ${theme.bgGradient} animate-gradientBackground`}></div>
      </div>

      <h1 className={`text-4xl font-extrabold mb-2 text-center ${theme.titleColor} ${theme.titleGlow}`}>
        {theme.paymentTitle}
      </h1>
      <p className="text-center text-gray-400 text-sm mb-10">
        {theme.flag} {config.name} only · {theme.currencySymbol} {config.currency}
      </p>

      <div className="sticky top-4 z-40 flex justify-center mb-10">
        <a
          href={NOMBA_PAYMENT_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full sm:w-96 bg-gradient-to-r from-green-400 to-green-600 hover:from-green-500 hover:to-green-700 p-3 rounded-2xl font-bold text-white text-lg shadow-md transition-transform hover:scale-105 flex items-center justify-center gap-2"
        >
          <FaCreditCard className="text-yellow-300" /> Pay with Nomba (Card / Bank / USSD)
        </a>
      </div>

      {vendors.length === 0 ? (
        <div className="text-center text-gray-400 py-12">
          <p className="text-lg">No vendors available in {config.name} right now.</p>
          <p className="text-sm mt-2">Check back later!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {vendors.map((vendor) => {
            const countdown = countdowns[vendor._id] || 0;
            const shrinkRatio = countdown > 0 ? Math.max(0.6, countdown / 300) : 1;
            const whatsappUrl = getWhatsappUrl(vendor.vendorDetails?.whatsapp, vendor.name);
            const online = onlineStatus[vendor._id]?.online ?? false;
            const orders = ordersSold[vendor._id] ?? 0;

            return (
              <motion.div
                key={vendor._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className={`relative bg-black/40 backdrop-blur-xl border ${theme.cardBorder} rounded-3xl shadow-xl p-6 flex flex-col justify-between hover:scale-105 transition-transform duration-300 ${theme.cardHoverShadow}`}
                style={{ transform: `scale(${shrinkRatio})`, transformOrigin: "top center" }}
              >
                <div className={`absolute top-4 right-4 bg-black/60 p-3 rounded-xl flex flex-col gap-1 text-right shadow-md border ${theme.infoBoxBorder}`}>
                  <p className={`text-sm font-semibold ${online ? "text-green-400" : "text-red-500"}`}>
                    {online ? "Online" : "Offline"}
                  </p>
                  <p className="text-sm text-yellow-400">Trustworthy: {vendor.trustworthy}%</p>
                  <p className={`text-sm ${theme.vendorNameColor}`}>Orders Sold: {orders}</p>
                </div>

                <div className="space-y-2">
                  <h2 className={`text-2xl font-bold ${theme.vendorNameColor} ${theme.vendorNameGlow}`}>
                    {vendor.name}
                  </h2>
                  <p className="text-gray-300">📞 {vendor.phone}</p>
                  <p className="text-gray-300">
                    💰 Rate:{" "}
                    <span className="text-yellow-400">
                      {theme.currencySymbol}{vendor.vendorDetails?.rate || 0}
                    </span>{" "}
                    per coin <FaCoins className="inline ml-1 text-yellow-400" />
                  </p>
                  <p className="text-gray-300">
                    📦 Stock: <span className="text-yellow-400">{vendor.vendorDetails?.stock || 0}</span>
                  </p>
                  <p className="text-gray-300">
                    🏦 {vendor.vendorDetails?.bankName || "N/A"} <br />
                    💳 {vendor.vendorDetails?.accountNumber || "N/A"}
                  </p>

                  <div className="mt-4 flex flex-col gap-3">
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold">
                        <FaCoins className="inline text-yellow-400" />
                      </span>
                      <input
                        type="number"
                        placeholder="Coins to buy"
                        value={amounts[vendor._id] || ""}
                        onChange={(e) =>
                          setAmounts((prev) => ({ ...prev, [vendor._id]: e.target.value }))
                        }
                        className={`w-full pl-9 pr-4 py-3 rounded-xl bg-black/50 border ${theme.inputBorder} text-white placeholder-gray-400 focus:ring-2 ${theme.inputFocus} outline-none transition`}
                      />
                    </div>
                    <button
                      onClick={() => setShowPaymentChoice(vendor)}
                      className={`w-full bg-gradient-to-r ${theme.buyBtn} p-3 rounded-2xl font-bold text-white shadow-lg transition-transform hover:scale-105`}
                    >
                      Buy Coins
                    </button>
                    {whatsappUrl && (
                      <a
                        href={whatsappUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full bg-gradient-to-r from-green-400 to-green-600 hover:from-green-500 hover:to-green-700 p-3 rounded-2xl font-bold text-white text-center shadow-lg transition-transform hover:scale-105"
                      >
                        WhatsApp
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* PAYMENT CHOICE MODAL */}
      {showPaymentChoice && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/70 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.35 }}
            className="bg-black/60 backdrop-blur-xl p-6 sm:p-8 rounded-3xl shadow-2xl text-center w-full max-w-sm"
          >
            <h2 className={`text-2xl sm:text-3xl font-bold ${theme.modalTitleColor} mb-6`}>
              Choose Payment Method
            </h2>

            <div className="flex flex-col gap-4">
              <button
                onClick={() => {
                  const vendor = showPaymentChoice;
                  const hasBankDetails =
                    vendor.vendorDetails?.bankName &&
                    vendor.vendorDetails?.accountNumber;

                  if (!hasBankDetails) {
                    // Vendor has no bank details — toast then redirect to link flow
                    showToast(
                      "⚠️ Bank details unavailable, using payment link instead"
                    );
                    setShowPaymentChoice(null);
                    setTimeout(() => openLinkAmountModal(vendor), 800);
                    return;
                  }

                  // Bank details exist — use bank flow as normal
                  handleBuy(vendor);
                  setShowPaymentChoice(null);
                }}
                className={`w-full bg-gradient-to-r ${theme.payBankBtnGradient} p-3 rounded-2xl font-bold text-white text-lg shadow-md transition-transform hover:scale-105 flex items-center justify-center gap-2`}
              >
                <FaCoins /> {isGhana ? "Pay via Bank / MoMo" : "Pay via Bank Transfer"}
              </button>

              <button
                onClick={() => {
                  const vendor = showPaymentChoice;
                  setShowPaymentChoice(null);
                  openLinkAmountModal(vendor);
                }}
                className={`w-full p-3 rounded-2xl font-bold text-lg shadow-md transition-transform flex items-center justify-center gap-2 bg-gradient-to-r ${theme.primaryGradient} hover:scale-105 text-white`}
              >
                <FaCreditCard />{" "}
                {showPaymentChoice.vendorDetails?.paymentLink
                  ? "Pay via Vendor Link"
                  : "Pay via Card / Bank / USSD"}
              </button>

              <button
                onClick={() => setShowPaymentChoice(null)}
                className="text-gray-400 hover:text-white mt-4 text-sm underline"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* AMOUNT INPUT */}
      {showLinkAmountModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/70 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-black/70 backdrop-blur-xl p-6 rounded-3xl shadow-2xl w-full max-w-sm space-y-4 border border-white/10"
          >
            <div className="text-center">
              <div className="text-4xl mb-2">💵</div>
              <h2 className={`text-xl font-bold ${theme.modalTitleColor}`}>
                How much do you want to pay?
              </h2>
              <p className="text-gray-400 text-xs mt-1">
                Rate: {theme.currencySymbol}{showLinkAmountModal.vendorDetails?.rate} per coin
              </p>
            </div>

            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold">
                {theme.currencySymbol}
              </span>
              <input
                type="number"
                placeholder="Enter amount in money"
                value={moneyAmounts[showLinkAmountModal._id] || ""}
                onChange={(e) =>
                  setMoneyAmounts((prev) => ({
                    ...prev,
                    [showLinkAmountModal._id]: e.target.value,
                  }))
                }
                className={`w-full pl-9 pr-4 py-3 rounded-xl bg-black/50 border ${theme.inputBorder} text-white placeholder-gray-400 focus:ring-2 ${theme.inputFocus} outline-none transition`}
              />
            </div>

            {moneyAmounts[showLinkAmountModal._id] &&
              Number(moneyAmounts[showLinkAmountModal._id]) > 0 &&
              Number(showLinkAmountModal.vendorDetails?.rate) > 0 && (
                <div className="p-3 rounded-xl bg-green-500/10 border border-green-400/30 text-center">
                  <p className="text-xs text-green-300 mb-1">You will receive</p>
                  <p className="text-2xl font-bold text-green-400">
                    {(
                      Number(moneyAmounts[showLinkAmountModal._id]) /
                      Number(showLinkAmountModal.vendorDetails.rate)
                    ).toFixed(2)}{" "}
                    coins
                  </p>
                </div>
              )}

            <button
              onClick={proceedToLinkInstructions}
              className={`w-full bg-gradient-to-r ${theme.primaryGradient} p-3 rounded-2xl font-bold text-white shadow-lg transition-transform hover:scale-105`}
            >
              Continue
            </button>
            <button
              onClick={() => setShowLinkAmountModal(null)}
              className="w-full text-gray-400 hover:text-white text-sm underline"
            >
              Cancel
            </button>
          </motion.div>
        </div>
      )}

      {/* INSTRUCTIONS MODAL */}
      {showLinkInstructions && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/80 p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-black/70 backdrop-blur-xl p-6 rounded-3xl shadow-2xl w-full max-w-md space-y-5 border border-white/10 my-4"
          >
            <div className="text-center">
              <div className="text-4xl mb-1">📋</div>
              <h2 className="text-xl font-bold text-white">Follow These Steps</h2>
              <p className="text-gray-400 text-xs mt-1">Takes 30 seconds</p>
            </div>

            <div className="p-4 rounded-2xl bg-gradient-to-br from-green-500/20 to-emerald-500/10 border-2 border-green-400/40 space-y-3">
              <p className="text-center text-green-300 font-semibold text-sm">
                Step 1: Copy the amount
              </p>
              <button
                onClick={() => copyMoneyAmount(showLinkInstructions.moneyAmount)}
                className="w-full p-3 rounded-xl bg-black/40 border border-green-400/30 hover:bg-black/60 transition flex items-center justify-between"
              >
                <span className="text-xl font-bold text-green-400">
                  {theme.currencySymbol}
                  {showLinkInstructions.moneyAmount.toLocaleString()}
                </span>
                <span className="text-xs bg-green-500 text-black font-bold px-3 py-2 rounded-lg">
                  📋 TAP TO COPY
                </span>
              </button>
            </div>

            <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-400/30 space-y-2">
              <p className="text-blue-300 font-semibold text-sm">📲 On the payment page:</p>
              <ol className="text-blue-100 text-sm space-y-2 pl-1">
                <li>
                  <b className="text-blue-400">2.</b> Tap the{" "}
                  <span className="font-bold text-white bg-white/10 px-2 py-0.5 rounded">Amount</span>{" "}
                  field and <b className="text-white">paste</b>{" "}
                  <b className="text-green-300">
                    {showLinkInstructions.moneyAmount.toLocaleString()}
                  </b>
                </li>
                <li>
                  <b className="text-blue-400">3.</b> Choose your payment option (Card, Transfer, USSD)
                </li>
                <li>
                  <b className="text-blue-400">4.</b> Complete the payment
                </li>
                <li>
                  <b className="text-blue-400">5.</b> Come back to send your proof
                </li>
              </ol>
            </div>

            <div className="p-3 rounded-xl bg-yellow-500/15 border border-yellow-400/40 text-yellow-200 text-xs text-center">
              💡 Paste the exact amount: <b className="text-white">{theme.currencySymbol}{showLinkInstructions.moneyAmount.toLocaleString()}</b>
            </div>

            <button
              onClick={goToVendorLink}
              disabled={linkCountdown > 0}
              className={`w-full py-4 rounded-xl font-bold text-base shadow-lg ${
                linkCountdown > 0
                  ? "bg-gray-600 cursor-not-allowed text-gray-300"
                  : `bg-gradient-to-r ${theme.primaryGradient} hover:brightness-110 text-white active:scale-[0.98]`
              }`}
            >
              {linkCountdown > 0
                ? `Please read first... (${linkCountdown}s)`
                : "✅ Got It — Open Payment Page"}
            </button>

            <button
              onClick={() => setShowLinkInstructions(null)}
              className="w-full py-1 text-xs text-gray-400 hover:text-white"
            >
              ← Cancel
            </button>
          </motion.div>
        </div>
      )}

      {/* PENDING SCREEN */}
      {showLinkPending && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/80 p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-black/70 backdrop-blur-xl p-6 rounded-3xl shadow-2xl w-full max-w-md space-y-5 border border-yellow-500/30 my-4"
          >
            <div className="text-center">
              <div className="text-5xl mb-2">⏳</div>
              <h2 className="text-2xl font-bold text-yellow-400">Payment Pending</h2>
              <p className="text-gray-400 text-xs mt-1">
                Awaiting vendor confirmation
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-gradient-to-br from-yellow-500/15 to-yellow-600/5 border-2 border-yellow-400/40 text-center">
              <p className="text-yellow-200 text-sm mb-1">Once vendor confirms:</p>
              <p className="text-3xl font-bold text-yellow-400">
                {showLinkPending.coins.toLocaleString()} coins
              </p>
              <p className="text-yellow-200/70 text-xs mt-1">
                will be credited to your account
              </p>
            </div>

            <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Vendor</span>
                <span className="text-white font-medium">{showLinkPending.vendor.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Amount paid</span>
                <span className="text-white font-medium">
                  {theme.currencySymbol}
                  {showLinkPending.moneyAmount.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Rate</span>
                <span className="text-white font-medium">
                  {theme.currencySymbol}
                  {showLinkPending.vendor.vendorDetails?.rate} per coin
                </span>
              </div>
            </div>

            <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-400/30 text-blue-200 text-xs text-center">
              💡 For faster confirmation, send your payment proof to the vendor on WhatsApp
            </div>

            {showLinkPending.vendor.vendorDetails?.whatsapp && (
              <a
                href={getWhatsappUrl(
                  showLinkPending.vendor.vendorDetails.whatsapp,
                  showLinkPending.vendor.name,
                  `Hi ${showLinkPending.vendor.name}, I just paid ${theme.currencySymbol}${showLinkPending.moneyAmount.toLocaleString()} for ${showLinkPending.coins} coins. Please confirm my order. Thank you!`
                )}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 p-3 rounded-2xl font-bold text-white text-center shadow-lg flex items-center justify-center gap-2"
              >
                <FaWhatsapp className="text-xl" /> Message Vendor on WhatsApp
              </a>
            )}

            <button
              onClick={() => setShowLinkPending(null)}
              className="w-full py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-medium"
            >
              Close
            </button>
          </motion.div>
        </div>
      )}

      {/* ORDER MODAL (BANK FLOW) */}
      {showOrderModal && modalData && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/70 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.35 }}
            className="bg-black/60 backdrop-blur-xl p-6 sm:p-8 rounded-3xl shadow-2xl text-center w-full max-w-sm"
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-green-400 mb-6 animate-pulse">
              Order Created
            </h2>
            <div className="text-left space-y-4 text-white text-sm sm:text-base">
              <p>Make payment to the details below:</p>
              <p>
                Send Amount:{" "}
                <span className="text-yellow-400 font-semibold">
                  {theme.currencySymbol}
                  {(modalData.money || modalData.amount * (modalData.vendorBank?.rate || 1)).toLocaleString()}
                </span>
              </p>
              <p>
                Bank Name:{" "}
                <span className="text-yellow-400 font-semibold">
                  {modalData.vendorBank?.bankName || "N/A"}
                </span>
              </p>
              <p className="flex items-center justify-between">
                Account Number:{" "}
                <span className="text-yellow-400 font-semibold">
                  {modalData.vendorBank?.accountNumber || "N/A"}
                </span>
                <button
                  onClick={() => copyToClipboard(modalData.vendorBank?.accountNumber)}
                  className="ml-3 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-gray-900 font-bold px-3 py-1 rounded-lg flex items-center gap-1 transition-all"
                >
                  <FaCopy /> Copy
                </button>
              </p>
              <p>
                Vendor Phone:{" "}
                <span className="text-yellow-400 font-semibold">{modalData.vendor.phone}</span>
              </p>
              <p>
                Country:{" "}
                <span className="text-yellow-400 font-semibold">
                  {theme.flag} {config.name}
                </span>
              </p>
            </div>
            <button
              onClick={handleModalOk}
              className={`mt-6 w-full bg-gradient-to-r ${theme.modalBtnGradient} p-4 sm:p-5 rounded-2xl font-bold text-white text-lg sm:text-xl shadow-lg transition-transform hover:scale-105`}
            >
              OK
            </button>
          </motion.div>
        </div>
      )}

      <style>{`
        @keyframes gradientBackground {
          0% {background-position:0% 50%}
          50% {background-position:100% 50%}
          100% {background-position:0% 50%}
        }
        .animate-gradientBackground {
          background-size: 200% 200%;
          animation: gradientBackground 15s ease infinite;
        }
      `}</style>
    </div>
  );
};

export default PurchasePage;
